import { Box } from 'designSystem/components/Box'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from 'designSystem/components/Command'
import { Text } from 'designSystem/components/Text'
import { useCommandPalette } from 'features/commandPalette/contexts/CommandPaletteContext'
import { useCommandPaletteInteractions } from 'features/commandPalette/hooks/useCommandPaletteInteractions'
import { useCommandPaletteTargets } from 'features/commandPalette/hooks/useCommandPaletteTargets'
import { useHeldWhileClosed } from 'features/commandPalette/hooks/useHeldWhileClosed'
import { useOpenCommandPaletteTarget } from 'features/commandPalette/hooks/useOpenCommandPaletteTarget'
import { groupCommandPaletteTargets } from 'features/commandPalette/utils/commandPaletteTargets'
import { COMMAND_PALETTE_MODIFIER_LABEL } from 'features/commandPalette/utils/shortcut'
import { useNavbarConfig } from 'features/navbar/useNavbarConfig'
import { useOptionalWorkspaceTabsActions } from 'features/workspaceTabs/contexts/WorkspaceTabsContext'
import { Sparkles } from 'lucide-react'
import { type FC, Fragment, useMemo } from 'react'

const ASK_VALUE = 'ask-cantilever'

// Suggestion popups anchored to text fields (inline predictions, password
// managers) take the first Escape before the page sees it, and a search box
// has no use for any of them.
const SEARCH_INPUT_ATTRIBUTES = {
  writingsuggestions: 'false',
  'data-1p-ignore': 'true',
  'data-lpignore': 'true',
  'data-form-type': 'other',
}

/**
 * One search box for every page, setting, asset, model and document the user
 * can open. Enter with nothing matching hands the text to a new chat.
 */
export const CommandPalette: FC = () => {
  const { isOpen, closeCommandPalette, siteId, orgId } = useCommandPalette()
  const openTarget = useOpenCommandPaletteTarget()
  const tabActions = useOptionalWorkspaceTabsActions()
  const { chats, workspaceTabs } = useNavbarConfig({
    orgId: orgId ?? '',
    siteId,
  })
  const {
    query,
    setQuery,
    trimmedQuery,
    highlighted,
    setHighlighted,
    closesInstantly,
    handleOpenChange,
    selectFromRow,
    ask,
    rememberEnterModifier,
    rememberClickModifier,
    rememberPointerDown,
    closeIfFocusWasLost,
  } = useCommandPaletteInteractions({
    isOpen,
    close: closeCommandPalette,
    openTarget,
    openNewAgenticChat: tabActions?.openNewAgenticChat,
  })

  const { targets, isSearching } = useCommandPaletteTargets({
    query,
    siteId,
    orgId,
    enabled: isOpen,
  })
  const groups = useMemo(() => groupCommandPaletteTargets(targets), [targets])
  const canAsk = Boolean(tabActions && chats && trimmedQuery)
  const showAsk = canAsk && targets.length === 0 && !isSearching

  const resultsWhileOpen = (
    <>
      <CommandEmpty>
        {isSearching
          ? 'Searching…'
          : trimmedQuery
            ? `No results for “${trimmedQuery}”`
            : 'Nothing to show for this site'}
      </CommandEmpty>
      {groups.map((group, index) => (
        <Fragment key={group.kindLabel}>
          {index > 0 && <CommandSeparator />}
          <CommandGroup heading={group.kindLabel}>
            {group.targets.map((target) => {
              const Icon = target.icon
              return (
                <CommandItem
                  key={target.id}
                  value={target.id}
                  onSelect={() => selectFromRow(target)}>
                  <Icon />
                  <Text truncate>{target.label}</Text>
                </CommandItem>
              )
            })}
          </CommandGroup>
        </Fragment>
      ))}
      {showAsk && (
        <CommandGroup heading='Ask Cantilever'>
          <CommandItem value={ASK_VALUE} onSelect={ask}>
            <Sparkles />
            <Text truncate>Ask: “{trimmedQuery}”</Text>
          </CommandItem>
        </CommandGroup>
      )}
    </>
  )
  const results = useHeldWhileClosed(resultsWhileOpen, isOpen)

  return (
    <CommandDialog
      size='lg'
      instantClose={closesInstantly}
      open={isOpen}
      onOpenChange={handleOpenChange}
      title='Search'
      description='Search pages, assets and documents, or ask Cantilever a question.'
      commandProps={{
        shouldFilter: false,
        loop: true,
        vimBindings: false,
        value: highlighted,
        onValueChange: setHighlighted,
        onKeyDown: rememberEnterModifier,
        onClickCapture: rememberClickModifier,
        onPointerDownCapture: rememberPointerDown,
        onBlur: closeIfFocusWasLost,
      }}>
      <CommandInput
        inputSize='lg'
        value={query}
        onValueChange={setQuery}
        placeholder='Search pages, assets, documents…'
        {...SEARCH_INPUT_ATTRIBUTES}
      />
      <CommandList height='fixed'>{results}</CommandList>
      <Box
        display='flex'
        align='center'
        gap='lg'
        paddingX='lg'
        paddingY='sm'
        border='top'>
        <Text variant='caption' color='tertiary'>
          Enter opens here
        </Text>
        {workspaceTabs && (
          <Text variant='caption' color='tertiary'>
            {COMMAND_PALETTE_MODIFIER_LABEL}+Enter opens a new tab
          </Text>
        )}
        <Text variant='caption' color='tertiary'>
          Esc closes
        </Text>
      </Box>
    </CommandDialog>
  )
}
